import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNotEmpty, useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { CRMModelAPI } from '@bobex/react-core/domain/CRMModel/CRMModelAPI';
import { CRMCategoryModelAPI } from '@bobex/react-core/domain/CRMCategoryModel/CRMCategoryModelAPI';
import { CRMCategoryConstants } from '@bobex/react-core/domain/CRMCategoryModel/constants/CRMCategoryConstants';
import { CRMStatusModelAPI } from '@bobex/react-core/domain/CRMStatusModel/CRMStatusModelAPI';
import { CRMStatusConstants } from '@bobex/react-core/domain/CRMStatusModel/constants/CRMStatusConstants';
import { CRMRequestUpdateDTO } from '@bobex/react-core/domain/CRMModel/dto/CRMRequestUpdateDTO';
import { AccountService } from '@bobex/react-core/services/AccountService';
import CRMCommentModal from '@/pages/UserDetails/components/CRMCommentModal';

interface CRMCommentFormValues {
  title: string;
  problemDesc: string;
  solution: string;
  dateDue: Date | null;
  timeDue: string;
  statusId: string;
  categoryId: string;
  reminderStatus: boolean;
  addToGoogleCalendar: boolean;
}

interface CRMCommentModalContainerProps {
  opened: boolean;
  userId: number | null;
  partnerRfpId?: number | null;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Self-contained CRM Comment Modal.
 * Parent controls open state and provides userId. When partnerRfpId is given (e.g. opened from a
 * lead's panel), the created comment is linked to that lead (t_crm.PARTNERRFPID).
 */
export function CRMCommentModalContainer({
  opened,
  userId,
  partnerRfpId,
  onClose,
  onSuccess,
}: CRMCommentModalContainerProps) {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);
  const [statuses, setStatuses] = useState<{ value: string; label: string }[]>([]);

  const accountUser = AccountService.getUser();

  const form = useForm<CRMCommentFormValues>({
    mode: 'uncontrolled',
    initialValues: {
      title: '',
      problemDesc: '',
      solution: '',
      dateDue: null,
      timeDue: '',
      statusId: String(CRMStatusConstants.STATUS_NA),
      categoryId: String(CRMCategoryConstants.DEFAULT_CATEGORY),
      reminderStatus: false,
      addToGoogleCalendar: false,
    },
    validate: {
      title: isNotEmpty(t('Title cannot be empty')),
    },
  });

  // Fetch CRM categories and statuses on mount
  useEffect(() => {
    const fetchCrmData = async () => {
      try {
        const [cats, stats] = await Promise.all([
          CRMCategoryModelAPI.getCategories(),
          CRMStatusModelAPI.getCRMStatus(),
        ]);
        setCategories(
          cats.map((item: any) => ({
            value: item.value.toString(),
            label: t(item.label),
          }))
        );
        setStatuses(
          stats.map((item: any) => ({
            value: item.value.toString(),
            label: t(item.label),
          }))
        );
      } catch (e) {
        console.error('Failed to fetch CRM data', e);
      }
    };
    fetchCrmData();
  }, [t]);

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      form.reset();
    }
  }, [opened]);

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleSubmit = useCallback(
    async (values: CRMCommentFormValues) => {
      if (!userId || !accountUser) return;

      try {
        let reminderDate: Date | null = null;
        if (values.dateDue) {
          reminderDate = new Date(values.dateDue);
          if (values.timeDue) {
            const [hours, minutes] = values.timeDue.split(':').map(Number);
            reminderDate.setHours(hours, minutes, 0, 0);
          }
        }

        const request = new CRMRequestUpdateDTO({
          memberId: userId,
          adminId: accountUser.userID,
          title: values.title,
          problemDesc: values.problemDesc,
          solution: values.solution,
          // Options load async; guard against an empty Select value (parseInt('') is NaN) so we never
          // post an invalid category/status id — fall back to the create defaults.
          categoryId: parseInt(values.categoryId) || CRMCategoryConstants.DEFAULT_CATEGORY,
          statusId: parseInt(values.statusId) || CRMStatusConstants.STATUS_NA,
          reminderStatus: values.reminderStatus ? -1 : 0,
          reminderDate: reminderDate,
          commentId: 0,
          partnerRfpId: partnerRfpId ?? null,
          addToGoogleCalendar: values.addToGoogleCalendar,
        });

        await CRMModelAPI.createOrUpdateCRMComment(request);

        notifications.show({
          title: t('Success'),
          message: t('Comment created successfully'),
          autoClose: 3000,
          color: 'green',
        });

        handleClose();
        onSuccess?.();
      } catch (e) {
        console.error('Failed to create comment', e);
        notifications.show({
          title: t('Error'),
          message: t('Failed to create comment'),
          autoClose: 5000,
          color: 'red',
        });
      }
    },
    [userId, partnerRfpId, accountUser, handleClose, onSuccess, t]
  );

  return (
    <CRMCommentModal
      opened={opened}
      onClose={handleClose}
      onSubmit={handleSubmit}
      form={form}
      crmCategories={categories}
      crmStatus={statuses}
      isEditing={false}
    />
  );
}
